import { openDB } from 'idb'
import { EventChainItemInput } from '@app/services/eventChain/types'

export type EventWithKey = { key: IDBValidKey; value: EventChainItemInput }
type EventChainBufferRecord = { scopeKey: string; event: EventChainItemInput }

export type EventChainDBActions = {
  addEvent: (scopeKey: string, event: EventChainItemInput) => Promise<void>
  addEvents: (scopeKey: string, events: EventChainItemInput[]) => Promise<void>
  getAllEventsWithKeys: (scopeKey: string) => Promise<EventWithKey[]>
  deleteByKeys: (keys: IDBValidKey[]) => Promise<void>
  clearBuffer: (scopeKey: string) => Promise<void>
  getBufferCount: (scopeKey?: string) => Promise<number>
  getMeta: (scopeKey: string, key: string) => Promise<string | null>
  setMeta: (scopeKey: string, key: string, value: string | null) => Promise<void>
  clearMeta: (scopeKey: string) => Promise<void>
}

const DB_NAME = 'QTI_EventChain_DB'
const BUFFER_STORE = 'Buffer'
const META_STORE = 'Meta'
const BUFFER_SCOPE_INDEX = 'scopeKey'
const LEGACY_SCOPE_KEY = 'legacy'

const isBufferRecord = (value: unknown): value is EventChainBufferRecord =>
  typeof value === 'object' &&
  value !== null &&
  'scopeKey' in value &&
  'event' in value

const metaKey = (scopeKey: string, key: string): string => `${scopeKey}:${key}`

export const initEventChainDB = async (): Promise<EventChainDBActions> => {
  const db = await openDB(DB_NAME, 2, {
    upgrade(database, oldVersion, _newVersion, transaction) {
      if (!database.objectStoreNames.contains(BUFFER_STORE)) {
        database.createObjectStore(BUFFER_STORE, { autoIncrement: true })
      }

      if (!database.objectStoreNames.contains(META_STORE)) {
        database.createObjectStore(META_STORE)
      }

      if (oldVersion < 2) {
        const bufferStore = transaction.objectStore(BUFFER_STORE)
        if (!bufferStore.indexNames.contains(BUFFER_SCOPE_INDEX)) {
          bufferStore.createIndex(BUFFER_SCOPE_INDEX, 'scopeKey', { unique: false })
        }
      }
    },
  })

  const migrateLegacyRecords = async () => {
    try {
      const tx = db.transaction([BUFFER_STORE, META_STORE], 'readwrite')
      const bufferStore = tx.objectStore(BUFFER_STORE)
      const metaStore = tx.objectStore(META_STORE)
      const keys = await bufferStore.getAllKeys()
      const values = await bufferStore.getAll()

      for (let i = 0; i < keys.length; i += 1) {
        const value = values[i]
        if (!isBufferRecord(value)) {
          await bufferStore.put(
            { scopeKey: LEGACY_SCOPE_KEY, event: value as EventChainItemInput },
            keys[i]
          )
        }
      }

      const legacyTailId = await metaStore.get('tailId')
      if (legacyTailId !== undefined) {
        await metaStore.put(legacyTailId, metaKey(LEGACY_SCOPE_KEY, 'tailId'))
        await metaStore.delete('tailId')
      }

      const legacySpliceId = await metaStore.get('spliceId')
      if (legacySpliceId !== undefined) {
        await metaStore.put(legacySpliceId, metaKey(LEGACY_SCOPE_KEY, 'spliceId'))
        await metaStore.delete('spliceId')
      }

      await tx.done
    } catch (err) {
      console.error('EventChainDB migrateLegacyRecords error', err)
    }
  }

  await migrateLegacyRecords()

  const addEvent = async (scopeKey: string, event: EventChainItemInput) => {
    try {
      await db.add(BUFFER_STORE, { scopeKey, event })
    } catch (err) {
      console.error('EventChainDB addEvent error', err)
    }
  }

  const addEvents = async (scopeKey: string, events: EventChainItemInput[]) => {
    try {
      const tx = db.transaction(BUFFER_STORE, 'readwrite')
      for (const event of events) {
        tx.store.add({ scopeKey, event })
      }
      await tx.done
    } catch (err) {
      console.error('EventChainDB addEvents error', err)
    }
  }

  const getAllEventsWithKeys = async (scopeKey: string): Promise<EventWithKey[]> => {
    try {
      const tx = db.transaction(BUFFER_STORE, 'readonly')
      const index = tx.store.index(BUFFER_SCOPE_INDEX)
      const keys = await index.getAllKeys(scopeKey)
      const values = await index.getAll(scopeKey)
      return keys.map((key, i) => ({ key, value: values[i].event }))
    } catch (err) {
      console.error('EventChainDB getAllEventsWithKeys error', err)
      return []
    }
  }

  const deleteByKeys = async (keys: IDBValidKey[]) => {
    try {
      const tx = db.transaction(BUFFER_STORE, 'readwrite')
      for (const key of keys) {
        tx.store.delete(key)
      }
      await tx.done
    } catch (err) {
      console.error('EventChainDB deleteByKeys error', err)
    }
  }

  const clearBuffer = async (scopeKey: string) => {
    try {
      const tx = db.transaction(BUFFER_STORE, 'readwrite')
      const keys = await tx.store.index(BUFFER_SCOPE_INDEX).getAllKeys(scopeKey)
      for (const key of keys) {
        tx.store.delete(key)
      }
      await tx.done
    } catch (err) {
      console.error('EventChainDB clearBuffer error', err)
    }
  }

  const getBufferCount = async (scopeKey?: string): Promise<number> => {
    try {
      if (scopeKey) {
        return await db.countFromIndex(BUFFER_STORE, BUFFER_SCOPE_INDEX, scopeKey)
      }
      return await db.count(BUFFER_STORE)
    } catch (err) {
      console.error('EventChainDB getBufferCount error', err)
      return 0
    }
  }

  const getMeta = async (scopeKey: string, key: string): Promise<string | null> => {
    try {
      const value = await db.get(META_STORE, metaKey(scopeKey, key))
      return value ?? null
    } catch (err) {
      console.error('EventChainDB getMeta error', err)
      return null
    }
  }

  const setMeta = async (scopeKey: string, key: string, value: string | null) => {
    try {
      if (value === null) {
        await db.delete(META_STORE, metaKey(scopeKey, key))
      } else {
        await db.put(META_STORE, value, metaKey(scopeKey, key))
      }
    } catch (err) {
      console.error('EventChainDB setMeta error', err)
    }
  }

  const clearMeta = async (scopeKey: string) => {
    try {
      const tx = db.transaction(META_STORE, 'readwrite')
      await tx.store.delete(metaKey(scopeKey, 'tailId'))
      await tx.store.delete(metaKey(scopeKey, 'spliceId'))
      await tx.done
    } catch (err) {
      console.error('EventChainDB clearMeta error', err)
    }
  }

  return {
    addEvent,
    addEvents,
    getAllEventsWithKeys,
    deleteByKeys,
    clearBuffer,
    getBufferCount,
    getMeta,
    setMeta,
    clearMeta,
  }
}
